import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Shield, TrendingUp, DollarSign, BarChart2, Zap, Users, AlertCircle, CheckCircle } from 'lucide-react';

const API = 'https://api.naijapredict.net';

const CSS = `
  .login-page { min-height:100vh; background:#07090a; display:flex; flex-direction:column; font-family:'Inter',sans-serif; }
  .login-hero { background:linear-gradient(135deg,#0d1114 0%,#0a1a0f 100%); padding:60px 24px 48px; text-align:center; border-bottom:1px solid rgba(255,255,255,.06); }
  .login-card { max-width:440px; margin:0 auto; padding:24px 20px 48px; }
  .login-features { display:grid; grid-template-columns:1fr 1fr; gap:12px; padding:24px 20px; max-width:440px; margin:0 auto; }
  .feature-item { background:#0d1114; border:1px solid rgba(255,255,255,.06); border-radius:12px; padding:14px; }
  .login-stats { display:flex; justify-content:center; gap:32px; padding:20px 24px 32px; border-top:1px solid rgba(255,255,255,.06); }

  @media(min-width:900px) {
    .login-page { flex-direction:row; }
    .login-left { flex:1; display:flex; flex-direction:column; justify-content:center; padding:60px 48px; max-width:600px; border-right:1px solid rgba(255,255,255,.06); }
    .login-right { width:440px; display:flex; align-items:center; justify-content:center; padding:48px 40px; }
    .login-hero { display:none; }
    .login-card { padding:0; max-width:100%; }
    .login-features { display:none; }
    .login-stats { display:none; }
    .login-desktop-features { display:grid !important; }
    .login-desktop-stats { display:flex !important; }
  }
`;

const FEATURES = [
  { Icon:DollarSign, title:'Earn 0.5% Royalties',  desc:'On every trade on your markets' },
  { Icon:TrendingUp, title:'Zero Capital Risk',     desc:'Platform seeds your markets'   },
  { Icon:BarChart2,  title:'Real-time Analytics',   desc:'Track volume and earnings live' },
  { Icon:Users,      title:'Audience Monetization', desc:'Turn followers into income'     },
  { Icon:Zap,        title:'Instant Payouts',       desc:'Withdraw to bank or USDC'       },
  { Icon:Shield,     title:'Non-Custodial',         desc:'Smart contracts on blockchain'  },
];

export default function LoginPage() {
  const { setCreator } = useAuth();
  const navigate = useNavigate();
  const tgRef = useRef(null);
  const [status, setStatus] = useState('');
  const [error, setError]   = useState('');

  useEffect(() => {
    window.onTelegramAuth = async (user) => {
      setStatus('Connecting...');
      setError('');
      try {
        const res = await fetch(API + '/api/creator/auth', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(user),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Auth failed');
        localStorage.setItem('creator_token', data.token);
        setCreator(data.creator);
        navigate('/dashboard');
      } catch(e) {
        setError(e.message);
        setStatus('');
      }
    };

    if (tgRef.current && !tgRef.current.querySelector('script')) {
      const s = document.createElement('script');
      s.src = 'https://telegram.org/js/telegram-widget.js?22';
      s.setAttribute('data-telegram-login', 'NaijaPredict_Bot');
      s.setAttribute('data-size', 'large');
      s.setAttribute('data-onauth', 'onTelegramAuth(user)');
      s.setAttribute('data-request-access', 'write');
      s.async = true;
      tgRef.current.appendChild(s);
    }
  }, []);

  const LoginForm = () => (
    <div>
      {/* Beta Banner */}
      <div style={{ background:'rgba(245,200,66,.08)', border:'1px solid rgba(245,200,66,.15)', borderRadius:12, padding:'12px 14px', marginBottom:24, display:'flex', gap:10, alignItems:'flex-start' }}>
        <AlertCircle size={15} color="#f5c842" strokeWidth={2} style={{ flexShrink:0, marginTop:1 }}/>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:'#f5c842' }}>Beta — Under Active Development</div>
          <div style={{ fontSize:11, color:'#4a5a63', marginTop:2 }}>Some features may be limited. Full launch coming soon!</div>
        </div>
      </div>

      {/* Logo + Title */}
      <div style={{ textAlign:'center', marginBottom:28 }}>
        <img src="/logo.svg" alt="NaijaPredict" style={{ width:60, height:60, borderRadius:14, marginBottom:14, display:'block', margin:'0 auto 14px' }}/>
        <h2 style={{ fontSize:22, fontWeight:800, fontFamily:"'Syne',sans-serif", marginBottom:6 }}>Creator Sign In</h2>
        <p style={{ fontSize:13, color:'#4a5a63', lineHeight:1.6 }}>Connect your Telegram to access the creator dashboard</p>
      </div>

      {/* Telegram Widget */}
      <div style={{ display:'flex', justifyContent:'center', marginBottom:12 }} ref={tgRef}/>

      {status && (
        <div style={{ textAlign:'center', fontSize:13, color:'#00c566', marginBottom:12, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
          <Zap size={13}/>{status}
        </div>
      )}
      {error && (
        <div style={{ background:'rgba(255,68,68,.08)', border:'1px solid rgba(255,68,68,.2)', borderRadius:10, padding:'10px 14px', marginBottom:12, fontSize:12, color:'#ff4444', display:'flex', gap:8, alignItems:'center' }}>
          <AlertCircle size={13}/>{error}
        </div>
      )}

      {/* Trust Signals */}
      <div style={{ marginTop:20, display:'flex', flexDirection:'column', gap:8 }}>
        {[
          [Shield,       'Verified via Telegram — no password needed'],
          [Zap,          'Instant access to creator dashboard'],
          [CheckCircle,  'Start earning from your first market'],
        ].map(([Icon, text]) => (
          <div key={text} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#4a5a63' }}>
            <Icon size={13} color="#00c566" strokeWidth={2} style={{ flexShrink:0 }}/>
            {text}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <style>{CSS}</style>
      <div className="login-page">

        {/* ── MOBILE HERO ── */}
        <div className="login-hero">
          <img src="/logo.svg" alt="NaijaPredict" style={{ width:56, height:56, borderRadius:12, marginBottom:16, display:'block', margin:'0 auto 16px' }}/>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:900, fontSize:26, marginBottom:4 }}>NAIJAPREDICT</div>
          <div style={{ fontSize:12, color:'#00c566', fontWeight:600, letterSpacing:2, marginBottom:16 }}>CREATOR PORTAL</div>
          <p style={{ fontSize:14, color:'#4a5a63', lineHeight:1.7, maxWidth:320, margin:'0 auto' }}>
            Create prediction markets. Earn 0.5% royalties on every trade — with zero capital at risk.
          </p>
        </div>

        {/* ── MOBILE CARD ── */}
        <div className="login-card">
          <LoginForm/>
        </div>

        {/* ── MOBILE FEATURES ── */}
        <div className="login-features">
          {FEATURES.map(({ Icon, title, desc }) => (
            <div key={title} className="feature-item">
              <div style={{ width:32, height:32, borderRadius:8, background:'rgba(0,197,102,.08)', border:'1px solid rgba(0,197,102,.12)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:8 }}>
                <Icon size={15} color="#00c566" strokeWidth={2}/>
              </div>
              <div style={{ fontSize:12, fontWeight:700, color:'#e8eaeb', marginBottom:3 }}>{title}</div>
              <div style={{ fontSize:11, color:'#4a5a63' }}>{desc}</div>
            </div>
          ))}
        </div>

        {/* ── MOBILE STATS ── */}
        <div className="login-stats">
          {[['242+','Active Traders'],['$0','Creator Risk'],['0.5%','Your Royalty']].map(([v,l])=>(
            <div key={l} style={{ textAlign:'center' }}>
              <div style={{ fontSize:22, fontWeight:800, color:'#00c566', fontFamily:"'Syne',sans-serif" }}>{v}</div>
              <div style={{ fontSize:11, color:'#4a5a63', marginTop:2 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* ── DESKTOP LEFT ── */}
        <div className="login-left" style={{ display:'none' }}>
          <div style={{ marginBottom:40 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:32 }}>
              <img src="/logo.svg" alt="NaijaPredict" style={{ width:44, height:44, borderRadius:10 }}/>
              <div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:900, fontSize:20, color:'#fff' }}>NAIJAPREDICT</div>
                <div style={{ fontSize:11, color:'#00c566', fontWeight:600, letterSpacing:2 }}>CREATOR PORTAL</div>
              </div>
            </div>
            <h1 style={{ fontSize:40, fontWeight:900, lineHeight:1.2, marginBottom:14, fontFamily:"'Syne',sans-serif" }}>
              Monetize Your<br/>
              <span style={{ background:'linear-gradient(135deg,#00c566,#f5c842)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>Predictions</span>
            </h1>
            <p style={{ fontSize:15, color:'#4a5a63', lineHeight:1.7, maxWidth:420 }}>
              Create prediction markets for your audience. Earn 0.5% royalties on every trade — with zero capital at risk.
            </p>
          </div>

          {/* Desktop Features */}
          <div className="login-desktop-features" style={{ display:'none', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:40 }}>
            {FEATURES.map(({ Icon, title, desc }) => (
              <div key={title} style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
                <div style={{ width:36, height:36, borderRadius:10, background:'rgba(0,197,102,.08)', border:'1px solid rgba(0,197,102,.12)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <Icon size={16} color="#00c566" strokeWidth={2}/>
                </div>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:'#e8eaeb', marginBottom:2 }}>{title}</div>
                  <div style={{ fontSize:11, color:'#4a5a63', lineHeight:1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Stats */}
          <div className="login-desktop-stats" style={{ display:'none', gap:32 }}>
            {[['242+','Active Traders'],['$0','Creator Risk'],['0.5%','Your Royalty']].map(([v,l])=>(
              <div key={l}>
                <div style={{ fontSize:26, fontWeight:800, color:'#00c566', fontFamily:"'Syne',sans-serif" }}>{v}</div>
                <div style={{ fontSize:12, color:'#4a5a63' }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── DESKTOP RIGHT ── */}
        <div className="login-right" style={{ display:'none' }}>
          <div style={{ width:'100%', maxWidth:380, background:'#0d1114', border:'1px solid rgba(255,255,255,.06)', borderRadius:20, padding:32 }}>
            <LoginForm/>
          </div>
        </div>

      </div>
    </>
  );
}
